import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import AlertDialog from "./AlertDialog";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
  FiRefreshCw,
  FiSave,
  FiSend,
  FiEye,
  FiFileText,
  FiCheck,
} from "react-icons/fi";
import axios from "axios";
import { PDFDocument, PDFName, PDFNumber, PDFString } from "pdf-lib";
import SignatureCanvas from "react-signature-canvas";

const API_BASE = "https://rentdoc-a0d223e1499e.herokuapp.com/api";
const LOCAL_PDF_PATH = "/N1.pdf";

const ONTARIO_GUIDELINE_LIMIT = 1.9;

// ── Date helpers (calendar-date only: no time, no timezone conversion) ──

// Parse "YYYY-MM-DD" or an ISO string as a local calendar date.
// Only the date part is used, so "2027-01-31T00:00:00.000Z" is always Jan 31.
function parseDateOnly(val) {
  if (!val) return null;
  if (val instanceof Date) {
    return new Date(val.getFullYear(), val.getMonth(), val.getDate());
  }
  const [y, m, d] = String(val).split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
}

// 1st of the month after the given date (no setMonth overflow).
// Jan 31 -> Feb 1, Jan 30 -> Feb 1, Dec 15 -> Jan 1 (next year)
function getFirstOfNextMonth(date) {
  const d = parseDateOnly(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 1);
}

// End = same calendar day 12 months later, minus one day.
// 2027-02-01 -> 2028-01-31, 2026-04-01 -> 2027-03-31, 2027-02-15 -> 2028-02-14
function getLastDayOneYearLater(startDate) {
  const d = parseDateOnly(startDate);
  return new Date(d.getFullYear() + 1, d.getMonth(), d.getDate() - 1);
}

function formatDateISO(date) {
  const d = parseDateOnly(date);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateDisplay(date) {
  if (!date) return "—";
  return formatDateISO(date);
}

const setFieldFontSize = (field, size) => {
  try {
    const rawField = field.acroField;
    const da = rawField.dict.get(PDFName.of("DA"));
    if (da) {
      const daStr = da.decodeText
        ? da.decodeText()
        : da.toString().replace(/[()]/g, "");
      const newDa = daStr.replace(/(\S+)\s+\d+(\.\d+)?\s+Tf/, `$1 ${size} Tf`);
      rawField.dict.set(PDFName.of("DA"), PDFString.of(newDa));
    }
  } catch (e) {
    console.warn("Could not set font size:", e.message);
  }
};

function getGlAccountId(rt) {
  return (
    rt.offsettingGLAccountId ||
    (rt.lines?.length > 0 && rt.lines[0].GLAccountId) ||
    null
  );
}

function getFullAddress(address) {
  if (!address || typeof address !== "object") return "";
  return [
    address.AddressLine1,
    address.AddressLine2,
    address.AddressLine3,
    address.City,
    address.State || address.StateProvince,
    address.PostalCode || address.ZipCode,
  ]
    .filter(Boolean)
    .join(", ");
}

// ── Main Component ──────────────────────────────────────────────
export default function RenewalModal({
  open,
  onClose,
  lease,
  onRenewalSuccess,
  initialTab = "renewal",
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [noticeDate, setNoticeDate] = useState("");
  const [newRent, setNewRent] = useState("");
  const [renewalStartDate, setRenewalStartDate] = useState("");
  const [renewalEndDate, setRenewalEndDate] = useState("");
  const [chargeOverrides, setChargeOverrides] = useState([]);

  const [storedSignatures, setStoredSignatures] = useState({
    landlord: null,
    representative: null,
  });
  const [companyInfo, setCompanyInfo] = useState(null);

  const [signatureMode, setSignatureMode] = useState("stored");
  const [savingSignature, setSavingSignature] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const sigCanvas = useRef(null);
  const rolePhonesRef = useRef({ landlord: "", representative: "" });

  const [n1FormData, setN1FormData] = useState({
    tenantName: "",
    tenantAddress: "",
    landlordName: "",
    landlordAddress: "",
    rentalUnitAddress: "",
    effectiveDay: "",
    effectiveMonth: "",
    effectiveYear: "",
    newRent: "",
    rentPeriod: "1",
    rentPeriodOther: "",
    increaseAmount: "",
    increasePercent: "",
    guideline: "lessOrEqual",
    signerType: "landlord",
    signerName: "",
    signerPhone: "",
    signDate: new Date().toLocaleDateString("en-CA"),
    signatureDataUrl: "",
    repName: "",
    repLSUC: "",
    repCompany: "",
    repAddress: "",
    repPhone: "",
    repCity: "",
    repProvince: "ON",
    repPostalCode: "",
    repFax: "",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "error",
    onConfirm: () => {},
  });

  const increase = lease?.property?.yearlyIncrease || 0;
  const currentRent = lease?.rent || 0;
  const isFixedWithRollover = lease?.leaseType === "FixedWithRollover";

  // ── Detect if lease is already renewed ──
  const isAlreadyRenewed = useMemo(() => {
    return (lease?.buildiumRenewalHistory || []).some(
      (r) =>
        r.leaseStatus === "Future" ||
        (r.leaseFromDate && new Date(r.leaseFromDate) > new Date()),
    );
  }, [lease]);

  // Latest renewal record from Buildium history (newest-first from backend)
  const latestRenewal = useMemo(() => {
    if (!lease?.buildiumRenewalHistory?.length) return null;
    return lease.buildiumRenewalHistory[0];
  }, [lease]);

  const nonRentRecurring = useMemo(() => {
    const all = (lease?.recurringTransactions || []).filter(
      (rt) => !rt.rentId && !rt.isExpired && rt.transactionType !== "Credit",
    );

    // Deduplicate: keep only one entry per charge type (GL account + memo).
    // Priority:
    //   1. Entry whose firstOccurrenceDate == renewal start (the new post-renewal amount)
    //   2. Latest entry with firstOccurrenceDate <= today
    //   3. Latest entry overall
    const renewalStart = (() => {
      const h = lease?.buildiumRenewalHistory;
      if (!h?.length) return null;
      const d = h[0]?.leaseFromDate ? new Date(h[0].leaseFromDate) : null;
      if (d) d.setHours(0, 0, 0, 0);
      return d;
    })();

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const groups = {};
    for (const rt of all) {
      const glId =
        rt.offsettingGLAccountId ||
        (rt.lines?.length > 0 ? rt.lines[0].GLAccountId : null) ||
        "unknown";
      const memo = (rt.memo || "").toLowerCase().trim();
      const key = `${glId}:${memo}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(rt);
    }

    const result = [];
    for (const entries of Object.values(groups)) {
      if (entries.length === 1) {
        result.push(entries[0]);
        continue;
      }

      const sorted = [...entries].sort((a, b) => {
        const da = a.firstOccurrenceDate
          ? new Date(a.firstOccurrenceDate).getTime()
          : 0;
        const db = b.firstOccurrenceDate
          ? new Date(b.firstOccurrenceDate).getTime()
          : 0;
        return da - db;
      });

      // Priority 1: entry whose firstOccurrenceDate matches renewal start
      if (renewalStart) {
        const renewalEntry = sorted.find((rt) => {
          if (!rt.firstOccurrenceDate) return false;
          const d = new Date(rt.firstOccurrenceDate);
          d.setHours(0, 0, 0, 0);
          return d.getTime() === renewalStart.getTime();
        });
        if (renewalEntry) {
          result.push(renewalEntry);
          continue;
        }
      }

      // Priority 2: latest entry with firstOccurrenceDate <= today
      let active = null;
      for (const rt of sorted) {
        const d = rt.firstOccurrenceDate
          ? new Date(rt.firstOccurrenceDate)
          : null;
        if (d && d <= now) active = rt;
      }
      if (active) {
        result.push(active);
        continue;
      }

      // Priority 3: latest entry overall
      result.push(sorted[sorted.length - 1]);
    }

    return result;
  }, [lease]);

  // ── For already-renewed leases, derive pre/post recurring amounts ──
  // nonRentRecurring is already deduplicated to the currently-active entry per
  // charge type. For the N1 we need:
  //   preRenewalRecurring  — what the tenant paid BEFORE the renewal
  //   postRenewalRecurring — what the tenant pays AFTER the renewal (= nonRentRecurring)
  //
  // If the active entry started ON or AFTER the renewal date, it IS the new amount;
  // back-calculate the old amount by dividing out the yearly increase.
  // If it started BEFORE the renewal date, the renewal hasn't absorbed it yet
  // and both pre and post are the same.
  const { preRenewalRecurring, postRenewalRecurring } = useMemo(() => {
    if (!isAlreadyRenewed || !latestRenewal?.leaseFromDate) {
      return {
        preRenewalRecurring: nonRentRecurring,
        postRenewalRecurring: nonRentRecurring,
      };
    }

    const renewalStart = new Date(latestRenewal.leaseFromDate);
    renewalStart.setHours(0, 0, 0, 0);
    const increase = lease?.property?.yearlyIncrease || 0;

    const pre = nonRentRecurring.map((rt) => {
      const startDate = rt.firstOccurrenceDate
        ? new Date(rt.firstOccurrenceDate)
        : null;
      // Entry started at/after renewal → it's the new amount; back-calc old
      if (startDate && startDate >= renewalStart && increase > 0) {
        const preAmount =
          Math.round((rt.amount / (1 + increase / 100)) * 100) / 100;
        return { ...rt, amount: preAmount };
      }
      return rt;
    });

    // postRenewalRecurring = the deduplicated current amounts (already correct)
    return {
      preRenewalRecurring: pre,
      postRenewalRecurring: nonRentRecurring,
    };
  }, [isAlreadyRenewed, latestRenewal, nonRentRecurring, lease]);

  const resolvedOwner = useMemo(() => {
    const prop = lease?.property || {};
    if (prop.selectedOwnerBuildiumId && prop.owners?.length) {
      const found = prop.owners.find(
        (o) => o.buildiumId === prop.selectedOwnerBuildiumId,
      );
      if (found) return found;
    }
    return prop.owners?.[0] || null;
  }, [lease]);

  // ── Totals for the renewal tab (used only when NOT already renewed) ──
  const totalCurrentAll = useMemo(() => {
    const totalRecurring = nonRentRecurring.reduce(
      (sum, rt) => sum + (rt.amount || 0),
      0,
    );
    return currentRent + totalRecurring;
  }, [currentRent, nonRentRecurring]);

  const totalNewAll = useMemo(() => {
    const rentVal = Number(newRent) || 0;
    const totalNewRecurring = chargeOverrides.reduce(
      (sum, c) => sum + (Number(c.newAmount) || 0),
      0,
    );
    return rentVal + totalNewRecurring;
  }, [newRent, chargeOverrides]);

  const effectiveIncreasePercent = useMemo(() => {
    if (totalCurrentAll <= 0) return 0;
    return ((totalNewAll - totalCurrentAll) / totalCurrentAll) * 100;
  }, [totalNewAll, totalCurrentAll]);

  // Auto-switch guideline — only relevant when not already renewed
  useEffect(() => {
    if (isAlreadyRenewed) return;
    const rounded = Math.round(effectiveIncreasePercent * 10) / 10;
    setN1FormData((prev) => {
      if (rounded > ONTARIO_GUIDELINE_LIMIT) {
        if (prev.guideline === "lessOrEqual") {
          return { ...prev, guideline: "moreApproved" };
        }
      } else {
        if (prev.guideline !== "lessOrEqual") {
          return { ...prev, guideline: "lessOrEqual" };
        }
      }
      return prev;
    });
  }, [effectiveIncreasePercent, isAlreadyRenewed]);

  const isAboveGuideline =
    !isAlreadyRenewed &&
    Math.round(effectiveIncreasePercent * 10) / 10 > ONTARIO_GUIDELINE_LIMIT;

  // ── Fetch stored signatures ──
  useEffect(() => {
    if (!open || !lease) return;

    const fetchSignaturesAndCompany = async () => {
      const token = localStorage.getItem("token");
      const prop = lease.property || {};

      let ownerBuildiumId = prop.selectedOwnerBuildiumId;
      if (!ownerBuildiumId && prop.owners?.length) {
        ownerBuildiumId = prop.owners[0].buildiumId;
      }
      const managerBuildiumId = prop.rentalManager?.buildiumId;

      if (ownerBuildiumId || managerBuildiumId) {
        try {
          const params = new URLSearchParams();
          if (ownerBuildiumId) params.set("ownerBuildiumId", ownerBuildiumId);
          if (managerBuildiumId)
            params.set("managerBuildiumId", managerBuildiumId);

          const res = await axios.get(
            `${API_BASE}/buildium/signatures/by-role?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (res.data?.signatures) {
            const sigs = res.data.signatures;
            setStoredSignatures(sigs);

            rolePhonesRef.current = {
              landlord: sigs.landlord?.phone || "",
              representative: sigs.representative?.phone || "",
            };

            const signerType = prop.n1SignerType || "landlord";
            const activeSignature = sigs[signerType];
            setSignatureMode(activeSignature?.signatureUrl ? "stored" : "draw");
          }
        } catch (err) {
          console.warn("Could not fetch stored signatures:", err.message);
          setSignatureMode("draw");
        }
      }

      try {
        const res = await axios.get(
          `${API_BASE}/company-profile/company-info`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCompanyInfo(res.data || null);
      } catch (err) {
        console.warn("Could not fetch company info:", err.message);
      }
    };

    fetchSignaturesAndCompany();
  }, [open, lease]);

  // ── Load saved customizations on open ──
  useEffect(() => {
    if (!lease || !open) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveTab(initialTab || "renewal");
      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `${API_BASE}/buildium/leases/${lease._id}/renewal-customizations`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const customs = res.data.renewalCustomizations;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (customs) {
          setNoticeDate(
            customs.noticeDate
              ? formatDateISO(customs.noticeDate)
              : formatDateISO(today),
          );
          setNewRent(
            customs.newRent !== undefined
              ? String(customs.newRent)
              : calcDefaultRent(),
          );
          setRenewalStartDate(
            customs.renewalStartDate
              ? formatDateISO(customs.renewalStartDate)
              : "",
          );
          setRenewalEndDate(
            customs.renewalEndDate ? formatDateISO(customs.renewalEndDate) : "",
          );

          const savedOverrides = customs.recurringChargeOverrides || [];
          setChargeOverrides(
            nonRentRecurring.map((rt) => {
              const saved = savedOverrides.find(
                (o) => o.buildiumTransactionId === rt.buildiumTransactionId,
              );
              return {
                buildiumTransactionId: rt.buildiumTransactionId,
                memo: rt.memo || rt.transactionType || "Charge",
                currentAmount: rt.amount,
                hasGlAccount: !!getGlAccountId(rt),
                newAmount: saved
                  ? String(saved.newAmount)
                  : calcIncreasedAmount(rt.amount),
              };
            }),
          );
        } else {
          resetToDefaults(today);
        }
      } catch {
        resetToDefaults(new Date());
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [lease, open, initialTab]);

  // ── Pre-fill N1 form data when lease/renewal data changes ──
  useEffect(() => {
    if (!lease || !open) return;

    const prop = lease.property || {};
    const signerType = prop.n1SignerType || "landlord";

    let owner = null;
    if (prop.selectedOwnerBuildiumId && prop.owners?.length) {
      owner = prop.owners.find(
        (o) => o.buildiumId === prop.selectedOwnerBuildiumId,
      );
    }
    if (!owner && prop.owners?.length) owner = prop.owners[0];

    let landlordName = "";
    let landlordOwnerAddress = "";
    if (owner) {
      landlordName = owner.isCompany
        ? owner.companyName
        : `${owner.firstName || ""} ${owner.lastName || ""}`.trim();
      // Build owner's mailing address for the "From" block when signer = landlord
      landlordOwnerAddress = getFullAddress(owner.address);
    }

    const fullPropertyAddress = getFullAddress(prop.address);
    const unitAddress = lease.unitNumber
      ? `${lease.unitNumber} - ${fullPropertyAddress}`
      : fullPropertyAddress;

    // After
    const tenantNames =
      lease.tenants
        ?.filter((t) => t.status === "Current" || !t.status)
        .map((t) => `${t.firstName} ${t.lastName}`)
        .join(" & ") || "";

    let signerName = landlordName;
    let repName = "";
    if (signerType === "representative" && prop.rentalManager) {
      const mgr = prop.rentalManager;
      signerName = mgr.isCompany
        ? mgr.companyName
        : `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim();
      repName = signerName;
    }

    let repCompany = "";
    let repAddress = "";
    let repPhone = "";
    let repCity = "";
    let repProvince = "ON";
    let repPostalCode = "";
    if (companyInfo) {
      repCompany = companyInfo.companyName || "";
      repPhone = companyInfo.phoneNumber || "";
      const address = companyInfo.address || {};
      repAddress = address.street || "";
      repCity = address.city || "";
      const zip = address.zip || "";
      if (zip) {
        const parts = zip.trim().split(/\s+/);
        if (parts.length >= 2) {
          repProvince = parts[0];
          repPostalCode = parts.slice(1).join(" ");
        } else {
          repPostalCode = zip.trim();
        }
      }
    }

    const signerPhone = rolePhonesRef.current[signerType] || "";

    setN1FormData((prev) => ({
      ...prev,
      tenantName: tenantNames,
      tenantAddress: unitAddress,
      landlordName,
      landlordOwnerAddress, // ← add this
      landlordAddress: "",
      rentalUnitAddress: unitAddress,
      signerType,
      signerName,
      signerPhone: signerPhone || prev.signerPhone,
      repName,
      repCompany,
      repAddress,
      repPhone,
      repCity,
      repProvince,
      repPostalCode,
      signDate: new Date().toLocaleDateString("en-CA"),
    }));
  }, [lease, open, companyInfo, storedSignatures]);

  // ── Sync N1 rent/increase fields ──
  // If already renewed: use actual Buildium renewal values (no double-increase).
  //   previousTotal = currentRent (pre-renewal base) + pre-renewal recurring fees
  //   renewedTotal  = latestRenewal.rent (new base)  + post-renewal recurring fees
  // If not yet renewed: calculate from renewal tab totals
  useEffect(() => {
    if (!lease || !open) return;

    if (isAlreadyRenewed && latestRenewal) {
      const renewedRent = latestRenewal.rent || 0;

      // Pre-renewal recurring = what tenant was paying before renewal
      const totalPreRecurring = preRenewalRecurring.reduce(
        (sum, rt) => sum + (rt.amount || 0),
        0,
      );
      // Post-renewal recurring = what tenant will pay after renewal starts
      const totalPostRecurring = postRenewalRecurring.reduce(
        (sum, rt) => sum + (rt.amount || 0),
        0,
      );

      // lease.rent is already updated to the new rent by Buildium after renewal.
      // The actual pre-renewal rent is stored in our local renewalHistory as previousRent.
      // Fall back to back-calculating from the yearly increase if not available.
      const localHistory = lease.renewalHistory || [];
      const latestLocalRenewal =
        localHistory.length > 0 ? localHistory[localHistory.length - 1] : null;
      const increase = lease?.property?.yearlyIncrease || 0;
      const previousRent =
        latestLocalRenewal?.previousRent ||
        (increase > 0
          ? Math.round((renewedRent / (1 + increase / 100)) * 100) / 100
          : currentRent);

      const previousTotal = previousRent + totalPreRecurring;
      const renewedTotal = renewedRent + totalPostRecurring;

      const increaseDollar = renewedTotal - previousTotal;
      const increasePct =
        previousTotal > 0
          ? ((increaseDollar / previousTotal) * 100).toFixed(1)
          : "0.0";

      const roundedPct = Math.round(parseFloat(increasePct) * 10) / 10;

      setN1FormData((prev) => ({
        ...prev,
        newRent: renewedTotal.toFixed(2),
        increaseAmount: increaseDollar.toFixed(2),
        increasePercent: increasePct,
        guideline:
          roundedPct > ONTARIO_GUIDELINE_LIMIT ? "moreApproved" : "lessOrEqual",
      }));
      return;
    }

    // Not yet renewed — use live renewal tab values
    if (totalCurrentAll <= 0 && currentRent <= 0) return;
    const totalIncreaseDollar = totalNewAll - totalCurrentAll;
    const pct =
      totalCurrentAll > 0
        ? ((totalIncreaseDollar / totalCurrentAll) * 100).toFixed(1)
        : "0.0";

    setN1FormData((prev) => ({
      ...prev,
      newRent: totalNewAll.toFixed(2),
      increaseAmount: totalIncreaseDollar.toFixed(2),
      increasePercent: pct,
    }));
  }, [
    open,
    lease,
    isAlreadyRenewed,
    latestRenewal,
    totalNewAll,
    totalCurrentAll,
    currentRent,
    preRenewalRecurring,
    postRenewalRecurring,
  ]);

  // ── Sync effective date ──
  // If already renewed: use the actual renewal start from Buildium history
  // If not yet renewed: use computed dates from notice logic
  useEffect(() => {
    if (!lease || !open) return;

    let effectiveDate;

    if (isAlreadyRenewed && latestRenewal?.leaseFromDate) {
      effectiveDate = parseDateOnly(latestRenewal.leaseFromDate);
    } else {
      effectiveDate = computedDates.start;
    }

    if (effectiveDate) {
      setN1FormData((prev) => ({
        ...prev,
        effectiveDay: String(effectiveDate.getDate()).padStart(2, "0"),
        effectiveMonth: String(effectiveDate.getMonth() + 1).padStart(2, "0"),
        effectiveYear: String(effectiveDate.getFullYear()),
      }));
    }
  }, [
    open,
    lease,
    isAlreadyRenewed,
    latestRenewal,
    noticeDate,
    renewalStartDate,
    renewalEndDate,
  ]);

  function calcDefaultRent() {
    return String(
      Math.round((currentRent + currentRent * (increase / 100)) * 100) / 100,
    );
  }

  function calcIncreasedAmount(amount) {
    return String(Math.round((amount + amount * (increase / 100)) * 100) / 100);
  }

  function resetToDefaults(today) {
    setNoticeDate(formatDateISO(today));
    setNewRent(calcDefaultRent());
    setRenewalStartDate("");
    setRenewalEndDate("");

    const defaultNewRent = Number(calcDefaultRent()) || 0;
    const defaultOverrides = nonRentRecurring.map((rt) => ({
      buildiumTransactionId: rt.buildiumTransactionId,
      memo: rt.memo || rt.transactionType || "Charge",
      currentAmount: rt.amount,
      hasGlAccount: !!getGlAccountId(rt),
      newAmount: calcIncreasedAmount(rt.amount),
    }));
    setChargeOverrides(defaultOverrides);

    const totalCurrentRecurring = nonRentRecurring.reduce(
      (sum, rt) => sum + (rt.amount || 0),
      0,
    );
    const totalCurrent = currentRent + totalCurrentRecurring;
    const totalNewRecurring = defaultOverrides.reduce(
      (sum, c) => sum + (Number(c.newAmount) || 0),
      0,
    );
    const totalNew = defaultNewRent + totalNewRecurring;
    const effectivePct =
      totalCurrent > 0
        ? Math.round(((totalNew - totalCurrent) / totalCurrent) * 100 * 10) / 10
        : 0;

    const autoGuideline =
      effectivePct > ONTARIO_GUIDELINE_LIMIT ? "moreApproved" : "lessOrEqual";
    setN1FormData((prev) => ({ ...prev, guideline: autoGuideline }));
  }

  // ── Computed dates preview ──
  const computedDates = useMemo(() => {
    const notice = noticeDate
      ? parseDateOnly(noticeDate)
      : parseDateOnly(new Date());
    const leaseExpiry = lease?.leaseToDate
      ? parseDateOnly(lease.leaseToDate)
      : null;

    const ninetyDays = new Date(notice);
    ninetyDays.setDate(ninetyDays.getDate() + 90);

    let start;
    if (renewalStartDate) {
      start = parseDateOnly(renewalStartDate);
    } else if (leaseExpiry && leaseExpiry > ninetyDays) {
      start = getFirstOfNextMonth(leaseExpiry);
    } else {
      start = getFirstOfNextMonth(ninetyDays);
    }

    let end;
    if (renewalEndDate) {
      end = parseDateOnly(renewalEndDate);
    } else {
      end = getLastDayOneYearLater(start);
    }

    const isWithin90 = leaseExpiry && leaseExpiry <= ninetyDays;

    return { start, end, ninetyDays, isWithin90 };
  }, [noticeDate, renewalStartDate, renewalEndDate, lease]);

  // ── Save Customizations ──
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    const token = localStorage.getItem("token");

    try {
      await axios.put(
        `${API_BASE}/buildium/leases/${lease._id}/renewal-customizations`,
        {
          newRent: newRent ? Number(newRent) : undefined,
          renewalStartDate: renewalStartDate || undefined,
          renewalEndDate: renewalEndDate || undefined,
          noticeDate: noticeDate || undefined,
          recurringChargeOverrides: chargeOverrides.map((c) => ({
            buildiumTransactionId: c.buildiumTransactionId,
            newAmount: Number(c.newAmount),
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess("Customizations saved successfully.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save customizations.");
    } finally {
      setSaving(false);
    }
  };

  // ── Signature helpers ──
  const getActiveSignatureDataUrl = async () => {
    if (signatureMode === "stored") {
      const activeStored = storedSignatures[n1FormData.signerType];
      if (activeStored?.signatureUrl) {
        try {
          const response = await fetch(activeStored.signatureUrl);
          const blob = await response.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          console.warn("Could not fetch stored signature image:", err);
          return null;
        }
      }
    }
    return n1FormData.signatureDataUrl || null;
  };

  const embedSignatureToPdf = async (pdfDoc, signatureDataUrl) => {
    if (!signatureDataUrl) return;
    try {
      const response = await fetch(signatureDataUrl);
      const imageBytes = await response.arrayBuffer();

      let signatureImage;
      const dataUrlLower = signatureDataUrl.toLowerCase();
      if (
        dataUrlLower.includes("image/jpeg") ||
        dataUrlLower.includes(".jpg")
      ) {
        signatureImage = await pdfDoc.embedJpg(imageBytes);
      } else {
        signatureImage = await pdfDoc.embedPng(imageBytes);
      }

      let placed = false;
      try {
        const form = pdfDoc.getForm();
        const sigField = form.getTextField(
          "form1[0].#subform[5].SignatureN1ToN3[0].Signature[0]",
        );
        if (sigField) {
          sigField.setImage(signatureImage);
          placed = true;
        }
      } catch (e) {
        console.warn("Form field approach failed:", e.message);
      }

      if (!placed) {
        const pages = pdfDoc.getPages();
        const sigPage = pages[5] || pages[pages.length - 1];
        const dims = signatureImage.scale(0.35);
        sigPage.drawImage(signatureImage, {
          x: 80,
          y: 340,
          width: dims.width,
          height: dims.height,
        });
      }
    } catch (err) {
      console.error("Failed to embed signature:", err);
    }
  };

  const saveDrawnSignature = async () => {
    if (!n1FormData.signatureDataUrl) return;
    const prop = lease?.property || {};
    const signerType = n1FormData.signerType;

    let targetBuildiumId;
    if (signerType === "representative") {
      targetBuildiumId = prop.rentalManager?.buildiumId;
    } else {
      targetBuildiumId =
        prop.selectedOwnerBuildiumId || prop.owners?.[0]?.buildiumId;
    }
    if (!targetBuildiumId) return;

    setSavingSignature(true);
    const token = localStorage.getItem("token");

    try {
      const usersRes = await axios.get(`${API_BASE}/buildium/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let targetUser;
      if (signerType === "representative") {
        targetUser = usersRes.data?.find(
          (u) => u.buildiumId === targetBuildiumId,
        );
      } else {
        targetUser = usersRes.data?.find(
          (u) =>
            u.rentalOwnerId === targetBuildiumId ||
            u.buildiumId === targetBuildiumId,
        );
      }
      if (!targetUser) return;

      const res = await axios.post(
        `${API_BASE}/buildium/users/${targetUser._id}/signature`,
        { signatureDataUrl: n1FormData.signatureDataUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data?.success) {
        setStoredSignatures((prev) => ({
          ...prev,
          [signerType]: {
            userId: targetUser._id,
            buildiumId: targetUser.buildiumId,
            name: `${targetUser.firstName || ""} ${targetUser.lastName || ""}`.trim(),
            signatureUrl: res.data.signatureUrl,
            signatureUpdatedAt: res.data.signatureUpdatedAt,
          },
        }));
        setSignatureMode("stored");
      }
    } catch (err) {
      console.error("Failed to save signature:", err.message);
    } finally {
      setSavingSignature(false);
    }
  };

  const trySetCombAmount = (form, fieldName, val) => {
    try {
      const num = parseFloat(val) || 0;
      const formatted = num.toFixed(2);
      const field = form.getTextField(fieldName);
      const rawField = field.acroField;
      const currentFlags = rawField.getFlags();
      rawField.setFlags(currentFlags & ~16777216);
      rawField.dict.set(PDFName.of("Q"), PDFNumber.of(0));
      field.setText(formatted);
    } catch (e) {
      console.warn(`CombAmount "${fieldName}" failed:`, e.message);
    }
  };

  // ── Generate N1 PDF bytes ──
  const generateN1PdfBytes = async () => {
    const resp = await fetch(LOCAL_PDF_PATH);
    if (!resp.ok) throw new Error("Could not load N1 PDF template");
    const pdfBytes = await resp.arrayBuffer();

    const pdfDoc = await PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
    });
    const form = pdfDoc.getForm();

    // ── Recompute financial fields fresh at PDF generation time ──
    // This avoids any stale-closure / render-timing issues with n1FormData.
    let freshNewRent, freshIncreaseAmount, freshIncreasePercent, freshGuideline;
    let freshEffectiveDay, freshEffectiveMonth, freshEffectiveYear;

    if (isAlreadyRenewed && latestRenewal) {
      const renewedRent = latestRenewal.rent || 0;
      const totalPreRecurring = preRenewalRecurring.reduce(
        (sum, rt) => sum + (rt.amount || 0),
        0,
      );
      const totalPostRecurring = postRenewalRecurring.reduce(
        (sum, rt) => sum + (rt.amount || 0),
        0,
      );
      const localHistory = lease.renewalHistory || [];
      const latestLocal =
        localHistory.length > 0 ? localHistory[localHistory.length - 1] : null;
      const inc = lease?.property?.yearlyIncrease || 0;
      const previousRent =
        latestLocal?.previousRent ||
        (inc > 0
          ? Math.round((renewedRent / (1 + inc / 100)) * 100) / 100
          : currentRent);
      const previousTotal = previousRent + totalPreRecurring;
      const renewedTotal = renewedRent + totalPostRecurring;
      const increaseDollar = renewedTotal - previousTotal;
      const increasePct =
        previousTotal > 0
          ? ((increaseDollar / previousTotal) * 100).toFixed(1)
          : "0.0";
      freshNewRent = renewedTotal.toFixed(2);
      freshIncreaseAmount = increaseDollar.toFixed(2);
      freshIncreasePercent = increasePct;
      freshGuideline =
        Math.round(parseFloat(increasePct) * 10) / 10 > ONTARIO_GUIDELINE_LIMIT
          ? "moreApproved"
          : "lessOrEqual";

      // Date-only parse: no timezone shift
      const effDate = parseDateOnly(latestRenewal.leaseFromDate);
      freshEffectiveDay = String(effDate.getDate()).padStart(2, "0");
      freshEffectiveMonth = String(effDate.getMonth() + 1).padStart(2, "0");
      freshEffectiveYear = String(effDate.getFullYear());
    } else {
      const increaseDollar = totalNewAll - totalCurrentAll;
      const pct =
        totalCurrentAll > 0
          ? ((increaseDollar / totalCurrentAll) * 100).toFixed(1)
          : "0.0";
      freshNewRent = totalNewAll.toFixed(2);
      freshIncreaseAmount = increaseDollar.toFixed(2);
      freshIncreasePercent = pct;
      freshGuideline = n1FormData.guideline;
      freshEffectiveDay = n1FormData.effectiveDay;
      freshEffectiveMonth = n1FormData.effectiveMonth;
      freshEffectiveYear = n1FormData.effectiveYear;
    }

    // Merge fresh financial values over n1FormData for the rest of the fields
    const data = {
      ...n1FormData,
      newRent: freshNewRent,
      increaseAmount: freshIncreaseAmount,
      increasePercent: freshIncreasePercent,
      guideline: freshGuideline,
      effectiveDay: freshEffectiveDay,
      effectiveMonth: freshEffectiveMonth,
      effectiveYear: freshEffectiveYear,
    };

    const trySet = (fieldName, value) => {
      try {
        const field = form.getTextField(fieldName);
        if (field) field.setText(value || "");
      } catch (e) {
        console.warn(`TextField "${fieldName}" failed:`, e.message);
      }
    };

    const tryRadio = (fieldName, value) => {
      try {
        const field = form.getRadioGroup(fieldName);
        if (field) {
          const options = field.getOptions();
          if (options.includes(value)) field.select(value);
        }
      } catch (e) {
        console.warn(`RadioGroup "${fieldName}" failed:`, e.message);
      }
    };

    const tryCheck = (fieldName, shouldCheck = true) => {
      try {
        const field = form.getCheckBox(fieldName);
        if (field && shouldCheck) field.check();
      } catch (e) {
        console.warn(`CheckBox "${fieldName}" failed:`, e.message);
      }
    };

    const tenantField = form.getTextField(
      "form1[0].#subform[0].Notice_Name_And_Address_1[0].To_TenantName[0]",
    );
    tenantField.setText(
      data.tenantName + (data.tenantAddress ? "\n" + data.tenantAddress : ""),
    );
    setFieldFontSize(tenantField, 8);

    const landlordField = form.getTextField(
      "form1[0].#subform[0].Notice_Name_And_Address_1[0].From_LandlordName[0]",
    );

    // Resolve which address goes under the landlord name:
    // - representative signs → use rep's full address
    // - landlord signs       → use owner's mailing address
    // - manual override in `landlordAddress` always wins if present
    let resolvedLandlordAddress = data.landlordAddress;
    if (!resolvedLandlordAddress) {
      if (data.signerType === "representative") {
        resolvedLandlordAddress = [
          data.repAddress,
          data.repCity,
          data.repProvince,
          data.repPostalCode,
        ]
          .filter(Boolean)
          .join(", ");
      } else {
        resolvedLandlordAddress = data.landlordOwnerAddress || "";
      }
    }

    landlordField.setText(
      data.landlordName +
        (resolvedLandlordAddress ? "\n" + resolvedLandlordAddress : ""),
    );
    setFieldFontSize(landlordField, 8);

    const unitField = form.getTextField(
      "form1[0].#subform[0].Notice_Name_And_Address_1[0].RentUnitAddress[0]",
    );
    unitField.setText(data.rentalUnitAddress);
    setFieldFontSize(unitField, 8);

    trySet(
      "form1[0].#subform[0].StartDate[0]",
      `${data.effectiveDay}/${data.effectiveMonth}/${data.effectiveYear}`,
    );

    trySetCombAmount(
      form,
      "form1[0].#subform[0].RentIncAmount1[0]",
      data.newRent,
    );
    trySetCombAmount(
      form,
      "form1[0].#subform[0].RentIncAmount2[0]",
      data.increaseAmount,
    );
    trySet("form1[0].#subform[0].RentIncPercent[0]", data.increasePercent);

    tryRadio("form1[0].#subform[0].period[0].PaymentPeriodM[0]", "1");
    tryRadio("form1[0].#subform[0].period[1].PaymentPeriodM[0]", "1");

    if (data.guideline === "lessOrEqual") {
      tryRadio("form1[0].#subform[0].Check1[0]", "1");
    } else if (data.guideline === "moreApproved") {
      tryRadio("form1[0].#subform[0].Check2[0]", "1");
      tryCheck("form1[0].#subform[0].Check_2_1[0]", true);
    } else if (data.guideline === "moreApplied") {
      tryRadio("form1[0].#subform[0].Check2[0]", "1");
      tryCheck("form1[0].#subform[0].Check_2_2[0]", true);
    }

    tryRadio(
      "form1[0].#subform[5].SelectSign[0]",
      data.signerType === "landlord" ? "1" : "2",
    );
    trySet(
      "form1[0].#subform[5].SignatureN1ToN3[0].SignName[0]",
      data.signerName,
    );
    trySet(
      "form1[0].#subform[5].SignatureN1ToN3[0].SignPhoneNum[0]",
      data.signerPhone,
    );
    trySet(
      "form1[0].#subform[5].SignatureN1ToN3[0].SignDate[0]",
      data.signDate,
    );

    if (data.signerType === "representative") {
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentName[0]",
        data.repName,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentLSUC[0]",
        "",
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentCompany[0]",
        data.repCompany,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentAddress[0]",
        data.repAddress,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentPhoneNum[0]",
        data.repPhone,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentMunicipality[0]",
        data.repCity,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentProvince[0]",
        data.repProvince,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentPostCode[0]",
        data.repPostalCode,
      );
      trySet(
        "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentFaxNum[0]",
        data.repFax || "",
      );
    }

    const signatureDataUrl = await getActiveSignatureDataUrl();
    if (signatureDataUrl) {
      await embedSignatureToPdf(pdfDoc, signatureDataUrl);
    }

    return await pdfDoc.save();
  };

  // ── Preview N1 PDF ──
  const handlePreviewN1 = async () => {
    setGeneratingPdf(true);
    setError(null);
    try {
      const pdfBytes = await generateN1PdfBytes();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      console.error("PDF preview failed:", err);
      setError(err.message || "Failed to generate N1 PDF preview");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // ── Generate Renewal ──
  const handleGenerateRenewal = async () => {
    if (isAboveGuideline && n1FormData.guideline === "lessOrEqual") {
      setError(
        `The total increase exceeds the Ontario guideline of ${ONTARIO_GUIDELINE_LIMIT}%. Please select the appropriate guideline option in the N1 Form tab.`,
      );
      setActiveTab("n1form");
      return;
    }

    const chargesWithoutGl = chargeOverrides.filter((c) => !c.hasGlAccount);
    let warningMsg = "";
    if (chargesWithoutGl.length > 0) {
      warningMsg = `\n\n⚠ ${chargesWithoutGl.length} recurring charge(s) are missing GL Account IDs and will be skipped.`;
    }

    setConfirmDialog({
      open: true,
      title: "Confirm Lease Renewal",
      type: "error",
      message:
        `Are you sure you want to generate this renewal in Buildium?\n\n` +
        `New rent: $${Number(newRent).toFixed(2)}/mo\n` +
        `Start: ${formatDateDisplay(computedDates.start)}\n` +
        `End: ${formatDateDisplay(computedDates.end)}\n` +
        `Recurring charges to update: ${chargeOverrides.filter((c) => c.hasGlAccount).length}` +
        warningMsg +
        `\n\nThe N1 PDF will be generated and uploaded to the lease in Buildium.`,
      onConfirm: () => executeGenerateRenewal(),
    });
  };

  const executeGenerateRenewal = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);
    const token = localStorage.getItem("token");

    try {
      await axios.put(
        `${API_BASE}/buildium/leases/${lease._id}/renewal-customizations`,
        {
          newRent: newRent ? Number(newRent) : undefined,
          renewalStartDate: renewalStartDate || undefined,
          renewalEndDate: renewalEndDate || undefined,
          noticeDate: noticeDate || undefined,
          recurringChargeOverrides: chargeOverrides.map((c) => ({
            buildiumTransactionId: c.buildiumTransactionId,
            newAmount: Number(c.newAmount),
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      let n1PdfBlob = null;
      try {
        const pdfBytes = await generateN1PdfBytes();
        n1PdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      } catch (pdfErr) {
        console.warn(
          "N1 PDF generation failed, proceeding without it:",
          pdfErr.message,
        );
      }

      const formData = new FormData();
      if (n1PdfBlob) {
        formData.append(
          "n1Pdf",
          n1PdfBlob,
          `N1_RentIncrease_${lease.unitNumber || "unit"}.pdf`,
        );
      }

      const res = await axios.post(
        `${API_BASE}/buildium/leases/${lease._id}/generate-renewal`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const renewal = res.data.renewal;

      let n1UploadMsg = "";
      if (n1PdfBlob) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append(
            "file",
            n1PdfBlob,
            `N1_RentIncrease_${lease.unitNumber || "unit"}.pdf`,
          );
          const uploadRes = await axios.post(
            `${API_BASE}/buildium/leases/${lease._id}/upload-n1`,
            uploadFormData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            },
          );
          n1UploadMsg = uploadRes.data.buildiumFileId
            ? `\nN1 PDF uploaded to Buildium (File ID: ${uploadRes.data.buildiumFileId})`
            : "\nN1 PDF uploaded to Buildium.";
        } catch (uploadErr) {
          console.error("N1 Buildium upload failed:", uploadErr);
          n1UploadMsg = `\n\n⚠ N1 Buildium upload failed: ${uploadErr.message}\nYou can re-upload from the N1 Form tab.`;
        }
      }

      const emailResult = res.data.email || {};
      let emailMsg = "";
      if (emailResult.sent) {
        emailMsg = `\nRenewal notice email sent${emailResult.hasN1Attachment ? " with N1 PDF attached" : ""}.`;
      } else if (emailResult.reason) {
        emailMsg = `\n\n⚠ Email not sent: ${emailResult.reason}`;
      }

      setSuccess(
        `Renewal created successfully!` +
          `\nBuildium Renewal ID: ${renewal.buildiumRenewalId}` +
          `\nStart: ${formatDateDisplay(renewal.renewalStartDate)}` +
          `\nEnd: ${formatDateDisplay(renewal.renewalEndDate)}` +
          `\nNew Rent: $${renewal.newRent.toFixed(2)}/mo` +
          `\nRecurring charges updated: ${renewal.recurringChargesUpdated}` +
          n1UploadMsg +
          emailMsg,
      );

      if (onRenewalSuccess) onRenewalSuccess();
    } catch (err) {
      console.error("Renewal failed:", err);
      const details = err.response?.data?.details;
      let errorMsg =
        err.response?.data?.message ||
        "Failed to generate renewal in Buildium.";
      if (details?.UserFriendlyMessage) {
        errorMsg += `\n\nBuildium says: ${details.UserFriendlyMessage}`;
      }
      if (details?.Errors && Array.isArray(details.Errors)) {
        errorMsg += "\n" + details.Errors.map((e) => `• ${e}`).join("\n");
      }
      setError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleChargeOverride = (idx, value) => {
    setChargeOverrides((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], newAmount: value };
      return updated;
    });
  };

  const handleN1Change = (key, value) => {
    setN1FormData((prev) => ({ ...prev, [key]: value }));
  };

  if (!lease) return null;

  const totalCurrentRecurring = nonRentRecurring.reduce(
    (sum, rt) => sum + (rt.amount || 0),
    0,
  );
  const totalNewRecurring = chargeOverrides.reduce(
    (sum, c) => sum + (Number(c.newAmount) || 0),
    0,
  );
  const totalIncreaseDollar = totalNewAll - totalCurrentAll;
  const totalIncreasePercent =
    totalCurrentAll > 0
      ? ((totalIncreaseDollar / totalCurrentAll) * 100).toFixed(1)
      : "0.0";

  const activeStoredSig = storedSignatures[n1FormData.signerType];

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/40 transition-opacity" />
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-3xl rounded-xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <DialogTitle className="text-lg font-bold text-gray-900">
                Lease Renewal — {lease.unitNumber || "Unit"}{" "}
                <span className="text-sm font-normal text-gray-500">
                  ({lease.property?.name || "Property"})
                </span>
              </DialogTitle>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="flex border-b px-6">
              <button
                onClick={() => setActiveTab("renewal")}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === "renewal"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiRefreshCw className="inline w-4 h-4 mr-1.5" />
                Renewal Details
              </button>
              <button
                onClick={() => setActiveTab("n1form")}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === "n1form"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiFileText className="inline w-4 h-4 mr-1.5" />
                N1 Form
                {isAboveGuideline && n1FormData.guideline === "lessOrEqual" && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[65vh] overflow-y-auto px-6 py-5 space-y-6">
              {!isFixedWithRollover && (
                <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
                  <ExclamationTriangleIcon className="inline h-5 w-5 mr-1" />
                  Renewal is only available for{" "}
                  <strong>FixedWithRollover</strong> leases. This lease is{" "}
                  <strong>{lease.leaseType}</strong>.
                </div>
              )}

              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  <FiRefreshCw className="inline animate-spin mr-2" />
                  Loading...
                </div>
              ) : (
                <>
                  {/* ── RENEWAL TAB ── */}
                  {activeTab === "renewal" && (
                    <>
                      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm">
                        <div className="font-semibold text-blue-800 mb-2">
                          Current Lease
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-blue-700">
                          <div>
                            Property:{" "}
                            <strong>{lease.property?.name || "—"}</strong>
                          </div>
                          <div>
                            Unit: <strong>{lease.unitNumber || "—"}</strong>
                          </div>
                          <div>
                            Start:{" "}
                            <strong>
                              {formatDateDisplay(lease.leaseFromDate)}
                            </strong>
                          </div>
                          <div>
                            End:{" "}
                            <strong>
                              {formatDateDisplay(lease.leaseToDate)}
                            </strong>
                          </div>
                          <div>
                            Rent: <strong>${currentRent.toFixed(2)}/mo</strong>
                          </div>
                          <div>
                            Increase Rate: <strong>{increase}%</strong>
                          </div>
                          <div>
                            Tenants:{" "}
                            <strong>
                              {lease.tenants
                                ?.filter(
                                  (t) => t.status === "Current" || !t.status,
                                )
                                .map((t) => `${t.firstName} ${t.lastName}`)
                                .join(", ") || "—"}
                            </strong>
                          </div>
                          <div>
                            Buildium Lease ID:{" "}
                            <strong>{lease.buildiumLeaseId}</strong>
                          </div>
                        </div>
                      </div>

                      <FormSection title="Notice Date">
                        <p className="text-xs text-gray-500 mb-2">
                          The date the N1 notice was/will be given. Tenant must
                          have 90 days notice.
                        </p>
                        <input
                          type="date"
                          value={noticeDate}
                          onChange={(e) => setNoticeDate(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {computedDates.isWithin90 && (
                          <div className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            <ExclamationTriangleIcon className="inline h-4 w-4 mr-1" />
                            Lease expires within 90 days of notice. Start will
                            be pushed to 1st of month after the 90-day window.
                          </div>
                        )}
                      </FormSection>

                      <FormSection title="Renewal Dates">
                        <div className="rounded-lg bg-green-50 border border-green-200 p-3 mb-3">
                          <div className="grid grid-cols-2 gap-3 text-sm text-green-800">
                            <div>
                              <span className="text-xs font-medium text-green-600 uppercase">
                                New Start
                              </span>
                              <div className="font-bold text-lg">
                                {formatDateDisplay(computedDates.start)}
                              </div>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-green-600 uppercase">
                                New End
                              </span>
                              <div className="font-bold text-lg">
                                {formatDateDisplay(computedDates.end)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">
                          Override if needed (leave blank for auto-calculation):
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Override Start
                            </label>
                            <input
                              type="date"
                              value={renewalStartDate}
                              onChange={(e) =>
                                setRenewalStartDate(e.target.value)
                              }
                              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Override End
                            </label>
                            <input
                              type="date"
                              value={renewalEndDate}
                              onChange={(e) =>
                                setRenewalEndDate(e.target.value)
                              }
                              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        {(renewalStartDate || renewalEndDate) && (
                          <button
                            type="button"
                            onClick={() => {
                              setRenewalStartDate("");
                              setRenewalEndDate("");
                            }}
                            className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Reset to auto-calculated dates
                          </button>
                        )}
                      </FormSection>

                      <FormSection title="New Rent Amount">
                        <div className="grid grid-cols-2 gap-3 items-end">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              New Monthly Rent ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={newRent}
                              onChange={(e) => setNewRent(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div className="text-md text-gray-500 pb-2">
                            Current: ${currentRent.toFixed(2)} →{" "}
                            <span className="font-semibold text-green-700">
                              ${Number(newRent || 0).toFixed(2)}
                            </span>{" "}
                            <span className="text-md">
                              (+$
                              {(Number(newRent || 0) - currentRent).toFixed(2)})
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setNewRent(calcDefaultRent())}
                          className="mt-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Reset to {increase}% increase (${calcDefaultRent()})
                        </button>
                      </FormSection>

                      {chargeOverrides.length > 0 && (
                        <FormSection title="Recurring Charges (Non-Rent)">
                          <p className="text-xs text-gray-500 mb-3">
                            Updated amounts for each recurring charge.
                          </p>
                          <div className="space-y-2">
                            {chargeOverrides.map((charge, idx) => (
                              <div
                                key={charge.buildiumTransactionId}
                                className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${
                                  charge.hasGlAccount
                                    ? "bg-gray-50 border-gray-200"
                                    : "bg-yellow-50 border-yellow-200"
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-700 truncate">
                                    {charge.memo}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Current: ${charge.currentAmount?.toFixed(2)}
                                    {!charge.hasGlAccount && (
                                      <span className="ml-2 text-yellow-600 font-medium">
                                        ⚠ Missing GL Account — will be skipped
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">
                                    →
                                  </span>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                      $
                                    </span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={charge.newAmount}
                                      onChange={(e) =>
                                        handleChargeOverride(
                                          idx,
                                          e.target.value,
                                        )
                                      }
                                      disabled={!charge.hasGlAccount}
                                      className="w-28 border border-gray-300 rounded-lg py-1.5 pl-7 pr-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div className="flex justify-between pt-3 border-t border-gray-200 text-sm">
                              <span className="font-medium text-gray-700">
                                Total Recurring
                              </span>
                              <div>
                                <span className="text-gray-400 line-through mr-2">
                                  ${totalCurrentRecurring.toFixed(2)}
                                </span>
                                <span className="font-semibold text-green-700">
                                  ${totalNewRecurring.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </FormSection>
                      )}

                      {isAboveGuideline && (
                        <div className="rounded-lg bg-amber-50 border border-amber-300 p-4 text-sm text-amber-800">
                          <ExclamationTriangleIcon className="inline h-5 w-5 mr-1" />
                          <strong>Above Ontario Guideline:</strong> The total
                          increase of {totalIncreasePercent}% exceeds the{" "}
                          {ONTARIO_GUIDELINE_LIMIT}% guideline. Please review
                          the guideline selection in the{" "}
                          <button
                            onClick={() => setActiveTab("n1form")}
                            className="underline font-semibold"
                          >
                            N1 Form tab
                          </button>
                          .
                        </div>
                      )}

                      <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
                        <div className="text-sm font-semibold text-gray-800 mb-2">
                          Renewal Summary
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-xs text-gray-500 block">
                              Current Total
                            </span>
                            <span className="font-medium">
                              ${totalCurrentAll.toFixed(2)}/mo
                            </span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block">
                              New Total
                            </span>
                            <span className="font-bold text-green-700">
                              ${totalNewAll.toFixed(2)}/mo
                            </span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block">
                              Increase
                            </span>
                            <span className="font-bold text-blue-600">
                              +${totalIncreaseDollar.toFixed(2)} (
                              {totalIncreasePercent}%)
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              Buildium Lease ID: {lease.buildiumLeaseId}
                            </div>
                            <div>Rent Cycle: Monthly</div>
                            <div>SendWelcomeEmail: false</div>
                            <div>LeaseType: FixedWithRollover</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── N1 FORM TAB ── */}
                  {activeTab === "n1form" && (
                    <>
                      <div className="rounded-lg bg-orange-50 border border-orange-200 p-4 text-sm">
                        <div className="font-semibold text-orange-800 mb-1">
                          N1 — Rent Increase Notice
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-orange-700">
                          <div>
                            Unit: <strong>{lease.unitNumber || "—"}</strong>
                          </div>
                          <div>
                            Property:{" "}
                            <strong>{lease.property?.name || "Unknown"}</strong>
                          </div>
                          <div>
                            New Total: <strong>${n1FormData.newRent}/mo</strong>
                          </div>
                          <div>
                            Increase:{" "}
                            <strong>{n1FormData.increasePercent}%</strong> (+$
                            {n1FormData.increaseAmount})
                          </div>
                        </div>
                        {isAlreadyRenewed && (
                          <div className="mt-2 text-xs text-orange-600 bg-orange-100 rounded px-2 py-1">
                            Values sourced from confirmed Buildium renewal
                            record.
                          </div>
                        )}
                      </div>

                      <FormSection title="Tenant Information (To)">
                        <Field
                          label="Tenant Name(s)"
                          value={n1FormData.tenantName}
                          onChange={(v) => handleN1Change("tenantName", v)}
                        />
                        <Field
                          label="Tenant Address"
                          value={n1FormData.tenantAddress}
                          onChange={(v) => handleN1Change("tenantAddress", v)}
                          placeholder="Full address of the rental unit"
                        />
                      </FormSection>

                      <FormSection title="Landlord Information (From)">
                        <Field
                          label="Landlord Name"
                          value={n1FormData.landlordName}
                          onChange={(v) => handleN1Change("landlordName", v)}
                        />
                        <p className="text-xs text-gray-400 italic">
                          Address under landlord name is auto-filled from the{" "}
                          {n1FormData.signerType === "representative"
                            ? "representative's mailing address"
                            : "owner's mailing address"}
                          . Override here if needed:
                        </p>
                        <Field
                          label="Landlord Address (override)"
                          value={n1FormData.landlordAddress}
                          onChange={(v) => handleN1Change("landlordAddress", v)}
                          placeholder="Leave blank to use auto-resolved address"
                        />
                      </FormSection>

                      <FormSection title="Address of the Rental Unit">
                        <Field
                          label="Rental Unit Address"
                          value={n1FormData.rentalUnitAddress}
                          onChange={(v) =>
                            handleN1Change("rentalUnitAddress", v)
                          }
                        />
                      </FormSection>

                      <FormSection title="New Rent Details">
                        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 mb-3 text-xs text-blue-700">
                          {isAlreadyRenewed
                            ? "Values sourced from the confirmed Buildium renewal record."
                            : "These values are synced from the Renewal Details tab."}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <Field
                            label="Day (dd)"
                            value={n1FormData.effectiveDay}
                            onChange={(v) => handleN1Change("effectiveDay", v)}
                            maxLength={2}
                          />
                          <Field
                            label="Month (mm)"
                            value={n1FormData.effectiveMonth}
                            onChange={(v) =>
                              handleN1Change("effectiveMonth", v)
                            }
                            maxLength={2}
                          />
                          <Field
                            label="Year (yyyy)"
                            value={n1FormData.effectiveYear}
                            onChange={(v) => handleN1Change("effectiveYear", v)}
                            maxLength={4}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              New Total Rent ($)
                            </label>
                            <input
                              type="text"
                              value={n1FormData.newRent}
                              readOnly
                              className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm bg-gray-50 text-gray-600"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Increase Amount ($)
                            </label>
                            <input
                              type="text"
                              value={n1FormData.increaseAmount}
                              readOnly
                              className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm bg-gray-50 text-gray-600"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Increase (%)
                            </label>
                            <input
                              type="text"
                              value={n1FormData.increasePercent}
                              readOnly
                              className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm bg-gray-50 text-gray-600"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Period
                            </label>
                            <select
                              value={n1FormData.rentPeriod}
                              onChange={(e) =>
                                handleN1Change("rentPeriod", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm"
                            >
                              <option value="1">Per Month</option>
                              <option value="2">Per Week</option>
                              <option value="3">Other</option>
                            </select>
                          </div>
                        </div>
                      </FormSection>

                      <FormSection title="Guideline">
                        {isAboveGuideline && (
                          <div className="rounded-lg bg-red-50 border border-red-200 p-3 mb-3 text-sm text-red-700">
                            <ExclamationTriangleIcon className="inline h-4 w-4 mr-1" />
                            The total increase ({totalIncreasePercent}%) exceeds
                            the Ontario guideline of {ONTARIO_GUIDELINE_LIMIT}%.
                            You must select one of the options below indicating
                            approval status.
                          </div>
                        )}
                        <div className="space-y-2">
                          {[
                            {
                              value: "lessOrEqual",
                              label:
                                "This rent increase is ≤ the guideline (no order needed)",
                              disabled: isAboveGuideline,
                            },
                            {
                              value: "moreApproved",
                              label:
                                "This rent increase is > the guideline AND has been approved by order",
                              disabled: false,
                            },
                            {
                              value: "moreApplied",
                              label:
                                "This rent increase is > the guideline AND I have applied for approval",
                              disabled: false,
                            },
                          ].map((opt) => (
                            <label
                              key={opt.value}
                              className={`flex items-start gap-2 text-sm cursor-pointer ${
                                opt.disabled
                                  ? "opacity-40 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <input
                                type="radio"
                                name="guideline"
                                value={opt.value}
                                checked={n1FormData.guideline === opt.value}
                                disabled={opt.disabled}
                                onChange={(e) =>
                                  handleN1Change("guideline", e.target.value)
                                }
                                className="mt-0.5 text-orange-500 focus:ring-orange-500"
                              />
                              <span className="text-gray-700">{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </FormSection>

                      <FormSection title="Signature">
                        <div className="flex gap-4 mb-3">
                          {["landlord", "representative"].map((t) => (
                            <label
                              key={t}
                              className="flex items-center gap-2 text-sm cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="signerType"
                                value={t}
                                checked={n1FormData.signerType === t}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  handleN1Change("signerType", newType);
                                  const stored = storedSignatures[newType];
                                  setSignatureMode(
                                    stored?.signatureUrl ? "stored" : "draw",
                                  );
                                  const rolePhone =
                                    rolePhonesRef.current[newType];
                                  if (rolePhone)
                                    handleN1Change("signerPhone", rolePhone);
                                }}
                                className="text-orange-500 focus:ring-orange-500"
                              />
                              <span className="capitalize text-gray-700">
                                {t}
                              </span>
                            </label>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field
                            label="Name of Person Signing"
                            value={n1FormData.signerName}
                            onChange={(v) => handleN1Change("signerName", v)}
                          />
                          <Field
                            label="Phone Number"
                            value={n1FormData.signerPhone}
                            onChange={(v) => handleN1Change("signerPhone", v)}
                          />
                        </div>
                        <Field
                          label="Sign Date"
                          value={n1FormData.signDate}
                          onChange={(v) => handleN1Change("signDate", v)}
                          placeholder="yyyy-mm-dd"
                        />

                        <div className="mt-4">
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Signature Source
                          </label>
                          <div className="flex gap-3 mb-3">
                            <button
                              type="button"
                              onClick={() => setSignatureMode("stored")}
                              disabled={!activeStoredSig?.signatureUrl}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                signatureMode === "stored"
                                  ? "bg-orange-50 border-orange-300 text-orange-700"
                                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                              } ${!activeStoredSig?.signatureUrl ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                            >
                              {activeStoredSig?.signatureUrl ? (
                                <>
                                  <FiCheck className="inline w-3 h-3 mr-1" />
                                  Use Stored Signature
                                </>
                              ) : (
                                "No Stored Signature"
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setSignatureMode("draw")}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                signatureMode === "draw"
                                  ? "bg-orange-50 border-orange-300 text-orange-700"
                                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              Draw New Signature
                            </button>
                          </div>

                          {signatureMode === "stored" &&
                            activeStoredSig?.signatureUrl && (
                              <div className="border border-gray-200 rounded-xl bg-gray-50 p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs text-gray-500">
                                    Stored signature for{" "}
                                    <strong>{activeStoredSig.name}</strong>
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    Updated{" "}
                                    {activeStoredSig.signatureUpdatedAt
                                      ? new Date(
                                          activeStoredSig.signatureUpdatedAt,
                                        ).toLocaleDateString("en-CA")
                                      : "—"}
                                  </span>
                                </div>
                                <img
                                  src={activeStoredSig.signatureUrl}
                                  alt="Stored signature"
                                  className="max-h-24 mx-auto"
                                  style={{ imageRendering: "auto" }}
                                />
                              </div>
                            )}

                          {signatureMode === "draw" && (
                            <>
                              <div className="border-2 border-dashed border-gray-300 rounded-2xl bg-white p-4">
                                <SignatureCanvas
                                  ref={sigCanvas}
                                  penColor="black"
                                  canvasProps={{
                                    width: 560,
                                    height: 160,
                                    className:
                                      "border border-gray-200 rounded-xl",
                                  }}
                                  onEnd={() => {
                                    if (sigCanvas.current) {
                                      const dataUrl =
                                        sigCanvas.current.toDataURL(
                                          "image/png",
                                        );
                                      handleN1Change(
                                        "signatureDataUrl",
                                        dataUrl,
                                      );
                                    }
                                  }}
                                />
                              </div>
                              <div className="flex items-center gap-3 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    sigCanvas.current?.clear();
                                    handleN1Change("signatureDataUrl", "");
                                  }}
                                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                                >
                                  Clear
                                </button>
                                {n1FormData.signatureDataUrl && (
                                  <button
                                    type="button"
                                    onClick={saveDrawnSignature}
                                    disabled={savingSignature}
                                    className="text-sm text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50"
                                  >
                                    {savingSignature
                                      ? "Saving..."
                                      : "Save as stored signature"}
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </FormSection>

                      {n1FormData.signerType === "representative" && (
                        <FormSection title="Representative Information">
                          <div className="grid grid-cols-3 gap-3">
                            <Field
                              label="Name"
                              value={n1FormData.repName}
                              onChange={(v) => handleN1Change("repName", v)}
                            />
                            <Field
                              label="LSUC #"
                              value={n1FormData.repLSUC}
                              onChange={(v) => handleN1Change("repLSUC", v)}
                            />
                            <Field
                              label="Company"
                              value={n1FormData.repCompany}
                              onChange={(v) => handleN1Change("repCompany", v)}
                            />
                          </div>
                          <Field
                            label="Mailing Address"
                            value={n1FormData.repAddress}
                            onChange={(v) => handleN1Change("repAddress", v)}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <Field
                              label="Phone"
                              value={n1FormData.repPhone}
                              onChange={(v) => handleN1Change("repPhone", v)}
                            />
                            <Field
                              label="Fax"
                              value={n1FormData.repFax}
                              onChange={(v) => handleN1Change("repFax", v)}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <Field
                              label="City"
                              value={n1FormData.repCity}
                              onChange={(v) => handleN1Change("repCity", v)}
                            />
                            <Field
                              label="Province"
                              value={n1FormData.repProvince}
                              onChange={(v) => handleN1Change("repProvince", v)}
                            />
                            <Field
                              label="Postal Code"
                              value={n1FormData.repPostalCode}
                              onChange={(v) =>
                                handleN1Change("repPostalCode", v)
                              }
                            />
                          </div>
                        </FormSection>
                      )}

                      <div className="flex justify-center">
                        <button
                          onClick={handlePreviewN1}
                          disabled={generatingPdf}
                          className={`flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-lg transition ${
                            generatingPdf
                              ? "bg-gray-400 text-white cursor-wait"
                              : "bg-orange-500 hover:bg-orange-600 text-white"
                          }`}
                        >
                          {generatingPdf ? (
                            <>
                              <FiRefreshCw className="w-4 h-4 animate-spin" />
                              Generating PDF...
                            </>
                          ) : (
                            <>
                              <FiEye className="w-4 h-4" />
                              Preview N1 PDF
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg whitespace-pre-wrap">
                      <ExclamationTriangleIcon className="inline h-5 w-5 mr-1" />
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg whitespace-pre-wrap">
                      <CheckCircleIcon className="inline h-5 w-5 mr-1" />
                      {success}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t px-6 py-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-300"
              >
                Close
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving || loading || !isFixedWithRollover}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg disabled:opacity-50"
                >
                  {saving ? (
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <FiSave className="w-4 h-4" />
                  )}
                  Save
                </button>
                <button
                  onClick={handleGenerateRenewal}
                  disabled={
                    generating || loading || !isFixedWithRollover || !!success
                  }
                  className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg ${
                    generating || !isFixedWithRollover || !!success
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {generating ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Generate Renewal
                    </>
                  )}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>

      <AlertDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Generate Renewal"
        cancelText="Cancel"
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />
    </Dialog>
  );
}

// ── Reusable sub-components ──────────────────────────────────────
function FormSection({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-100">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder = "",
  maxLength,
  type = "text",
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
      />
    </div>
  );
}
